(ns ui.gallery.selection
  "The selection of gallery entries as pure functions over the gallery
   state map (the `:gallery` entry of the Replicant store):

   - `:selected` the set of selected story-media eids
   - `:range-anchor` the eid of the last plain click, the anchor of a
     subsequent shift-click range selection
   - `:last-selected` the last non-empty selection, which the selection
     bar shows while it slides out (so it never shows '0 selected')")

(defn remember-last
  [gallery]
  (if (seq (:selected gallery))
    (assoc gallery
           :last-selected
           (:selected gallery))
    gallery))

(defn clear
  "Deselects all gallery entries."
  [gallery]
  (-> gallery
      (assoc :selected #{})
      (dissoc :range-anchor)))

(defn toggle
  "Toggles the selection of a single gallery entry and remembers it as
   the anchor for a subsequent shift-click range selection."
  [gallery story-media-eid]
  (-> gallery
      (update :selected
              (fn [selected]
                (if (contains? selected
                               story-media-eid)
                  (disj selected
                        story-media-eid)
                  (conj (or selected #{})
                        story-media-eid))))
      (assoc :range-anchor story-media-eid)
      (remember-last)))

(defn shift-range-click?
  "True when a click with `shift?` held should extend the selection as a
   shift-click range selection: something is selected and an anchor
   from a previous plain click exists."
  [gallery shift?]
  (boolean
    (and shift?
         (seq (:selected gallery))
         (:range-anchor gallery))))

(defn apply-range
  "Applies the toggle of the entry `story-media-eid` to the `range-eids`
   between the anchor and the entry (the shift-click behavior known
   from macOS Finder or Gmail): shift-clicking an unselected entry
   selects the range in addition to the current selection, while
   shift-clicking a selected entry deselects the range. The anchor
   stays unchanged, so consecutive shift-clicks extend from the same
   anchor."
  [gallery story-media-eid range-eids]
  (let [deselect? (contains? (:selected gallery)
                             story-media-eid)]
    (-> gallery
        (update :selected
                (fn [selected]
                  (if deselect?
                    (apply disj
                           (or selected #{})
                           range-eids)
                    (into (or selected #{})
                          range-eids))))
        (remember-last))))

(defn select-all
  [gallery eids]
  (-> gallery
      (assoc :selected (set eids))
      (remember-last)))

(defn display-count
  "The count the selection bar shows: the current selection, or the
   last non-empty one while the bar slides out."
  [gallery]
  (count (if (seq (:selected gallery))
           (:selected gallery)
           (:last-selected gallery))))
